import React, { useEffect, useMemo, useState } from 'react';

/**
 * KOT/Bar Offer Master — admin UI for the QR Self-Order offer engine.
 * See docs/SRS_KOT_BAR_OFFER_ENGINE.md. Same screen mounts under
 * KOT & POS → Masters and Bar Mgmt → Masters; outlet filter picks
 * between restaurant / bar outlets for a given property.
 *
 * Supports 4 rule types:
 *   QTY_DISCOUNT   — Buy N of item X → percent/fixed discount
 *   BOGO           — Buy N of item X → item Y free
 *   COMBO_PRICE    — Bundle of items X+Y+Z at fixed price
 *   SPEND_THRESHOLD — Order ≥ ₹N → percent/fixed/free-item discount
 *
 * Accepts `selectedProperty` (object with chain_id+id) when coming from
 * KOT Masters Hub, or `selectedPropertyId` (string) from Bar Masters Hub.
 */

const RULE_TYPES = [
  { key: 'QTY_DISCOUNT', label: 'Buy N → Discount', icon: '🔢' },
  { key: 'BOGO', label: 'Buy 1, Get 1', icon: '🎁' },
  { key: 'COMBO_PRICE', label: 'Combo Price', icon: '🍱' },
  { key: 'SPEND_THRESHOLD', label: 'Spend Threshold', icon: '💰' },
];
const REWARD_TYPES = [
  { key: 'PERCENT', label: '% Discount' },
  { key: 'FIXED', label: '₹ Discount' },
  { key: 'FREE_ITEM', label: 'Free Item' },
  { key: 'BUNDLE_PRICE', label: 'Bundle Price' },
];
const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const emptyForm = () => ({
  id: null,
  title: '',
  description: '',
  rule_type: 'QTY_DISCOUNT',
  trigger_item_id: '',
  trigger_qty: 2,
  trigger_amount: '',
  trigger_items_json: [],
  reward_type: 'PERCENT',
  reward_value: 40,
  reward_item_id: '',
  reward_scope: 'QUALIFYING_ITEMS',
  valid_from: '', valid_to: '',
  valid_time_from: '', valid_time_to: '',
  valid_days_json: [],
  max_per_session: 1,
  show_on_cart: true,
  show_on_checkout: true,
  staff_pushable: true,
  is_active: true,
});

export default function KotOfferMaster({ token, selectedProperty, selectedPropertyId, selectedOutletId = '' }) {
  // Normalise inputs — KotMastersHub passes selectedProperty, BarMastersHub passes selectedPropertyId.
  const property = selectedProperty || (selectedPropertyId ? { id: selectedPropertyId } : null);
  const propertyId = property?.id || '';
  const chainIdFromProp = property?.chain_id || '';

  const [chainId, setChainId] = useState(chainIdFromProp);
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState(selectedOutletId || '');
  const [menuItems, setMenuItems] = useState([]);
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // If chain_id wasn't passed (Bar Mgmt path), look it up from accessible properties
  useEffect(() => {
    if (chainIdFromProp) { setChainId(chainIdFromProp); return; }
    if (!propertyId) return;
    (async () => {
      try {
        const r = await fetch('/api/properties/accessible', { headers: { Authorization: 'Bearer ' + token }});
        const props = await r.json();
        const match = (Array.isArray(props) ? props : []).find(p => String(p.id) === String(propertyId));
        if (match?.chain_id) setChainId(match.chain_id);
      } catch { /* silent */ }
    })();
  }, [propertyId, chainIdFromProp, token]);

  const scopeHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    'x-outlet-id': outletId,
    'Content-Type': 'application/json',
  });

  // Load outlets for this property
  useEffect(() => {
    if (!chainId || !propertyId) return;
    (async () => {
      try {
        const r = await fetch(`/api/v1/core/outlets?property_id=${encodeURIComponent(propertyId)}`, {
          headers: { Authorization: 'Bearer ' + token, 'x-chain-id': chainId, 'x-property-id': propertyId },
        });
        const data = await r.json();
        if (r.ok) {
          const list = (Array.isArray(data) ? data : []).filter(o => o.is_active !== false);
          setOutlets(list);
          // If no outlet selected yet, pick the first one
          if (!outletId && list.length) setOutletId(list[0].id);
        }
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, propertyId]);

  // Load menu items + offers when outlet changes
  useEffect(() => {
    if (!outletId) { setMenuItems([]); setOffers([]); return; }
    (async () => {
      try {
        const [mRes, oRes] = await Promise.all([
          fetch('/api/v1/kot/masters/menu-items?all=1', { headers: scopeHeaders() }),
          fetch('/api/v1/kot/masters/offers?all=1', { headers: scopeHeaders() }),
        ]);
        const m = await mRes.json();
        const o = await oRes.json();
        if (mRes.ok) setMenuItems(Array.isArray(m) ? m : []);
        if (oRes.ok) setOffers(Array.isArray(o) ? o : []);
      } catch (err) { setError('Failed to load — ' + err.message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletId]);

  const menuById = useMemo(() => {
    const m = {};
    for (const mi of menuItems) m[mi.id] = mi;
    return m;
  }, [menuItems]);

  const startCreate = () => { setForm(emptyForm()); setEditing(true); setError(''); setMessage(''); };
  const startEdit = (o) => {
    setForm({
      id: o.id,
      title: o.title || '',
      description: o.description || '',
      rule_type: o.rule_type || 'QTY_DISCOUNT',
      trigger_item_id: o.trigger_item_id || '',
      trigger_qty: o.trigger_qty || '',
      trigger_amount: o.trigger_amount || '',
      trigger_items_json: Array.isArray(o.trigger_items_json) ? o.trigger_items_json : [],
      reward_type: o.reward_type || 'PERCENT',
      reward_value: o.reward_value || '',
      reward_item_id: o.reward_item_id || '',
      reward_scope: o.reward_scope || 'QUALIFYING_ITEMS',
      valid_from: o.valid_from ? String(o.valid_from).slice(0, 10) : '',
      valid_to: o.valid_to ? String(o.valid_to).slice(0, 10) : '',
      valid_time_from: o.valid_time_from ? String(o.valid_time_from).slice(0, 5) : '',
      valid_time_to: o.valid_time_to ? String(o.valid_time_to).slice(0, 5) : '',
      valid_days_json: Array.isArray(o.valid_days_json) ? o.valid_days_json : [],
      max_per_session: o.max_per_session || 1,
      show_on_cart: o.show_on_cart !== false,
      show_on_checkout: o.show_on_checkout !== false,
      staff_pushable: o.staff_pushable !== false,
      is_active: o.is_active !== false,
    });
    setEditing(true); setError(''); setMessage('');
  };

  const save = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setLoading(true); setError('');
    try {
      const body = { ...form };
      // Empty-string → null for numeric/id fields
      ['trigger_item_id', 'trigger_amount', 'reward_item_id'].forEach(k => { if (body[k] === '') body[k] = null; });
      ['trigger_qty', 'reward_value', 'max_per_session'].forEach(k => {
        body[k] = body[k] === '' || body[k] == null ? null : Number(body[k]);
      });
      ['valid_from', 'valid_to', 'valid_time_from', 'valid_time_to'].forEach(k => { if (body[k] === '') body[k] = null; });
      if (!Array.isArray(body.trigger_items_json) || !body.trigger_items_json.length) body.trigger_items_json = null;
      if (!Array.isArray(body.valid_days_json) || !body.valid_days_json.length) body.valid_days_json = null;

      const url = form.id ? `/api/v1/kot/masters/offers/${form.id}` : '/api/v1/kot/masters/offers';
      const method = form.id ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: scopeHeaders(), body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Save failed');
      setMessage(form.id ? 'Offer updated' : 'Offer created');
      setEditing(false);
      // reload
      const oRes = await fetch('/api/v1/kot/masters/offers?all=1', { headers: scopeHeaders() });
      const o = await oRes.json();
      if (oRes.ok) setOffers(Array.isArray(o) ? o : []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const deactivate = async (o) => {
    if (!window.confirm(`Deactivate offer "${o.title}"?`)) return;
    try {
      const r = await fetch(`/api/v1/kot/masters/offers/${o.id}`, { method: 'DELETE', headers: scopeHeaders() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      setMessage('Offer deactivated');
      const oRes = await fetch('/api/v1/kot/masters/offers?all=1', { headers: scopeHeaders() });
      setOffers(await oRes.json());
    } catch (err) { setError(err.message); }
  };

  const toggleDay = (d) => {
    setForm(f => {
      const set = new Set(f.valid_days_json || []);
      if (set.has(d)) set.delete(d); else set.add(d);
      return { ...f, valid_days_json: Array.from(set) };
    });
  };
  const addComboLine = () => setForm(f => ({ ...f, trigger_items_json: [...(f.trigger_items_json || []), { menu_item_id: '', qty: 1 }] }));
  const updateComboLine = (idx, patch) => setForm(f => {
    const arr = [...(f.trigger_items_json || [])];
    arr[idx] = { ...arr[idx], ...patch };
    return { ...f, trigger_items_json: arr };
  });
  const removeComboLine = (idx) => setForm(f => {
    const arr = [...(f.trigger_items_json || [])];
    arr.splice(idx, 1);
    return { ...f, trigger_items_json: arr };
  });

  const describeOffer = (o) => {
    const item = o.trigger_item_id ? menuById[o.trigger_item_id]?.name || 'item' : null;
    const rItem = o.reward_item_id ? menuById[o.reward_item_id]?.name || 'item' : null;
    if (o.rule_type === 'QTY_DISCOUNT') return `Buy ${o.trigger_qty || 0} × ${item || '?'} → ${o.reward_type === 'PERCENT' ? `${o.reward_value}% off` : `₹${o.reward_value} off`}`;
    if (o.rule_type === 'BOGO') return `Buy ${o.trigger_qty || 0} × ${item || '?'} → ${rItem || '?'} free`;
    if (o.rule_type === 'COMBO_PRICE') return `Combo of ${(o.trigger_items_json || []).length} items → ₹${o.reward_value} bundle`;
    if (o.rule_type === 'SPEND_THRESHOLD') return `Spend ≥ ₹${o.trigger_amount || 0} → ${o.reward_type === 'PERCENT' ? `${o.reward_value}% off` : o.reward_type === 'FREE_ITEM' ? `${rItem || 'free item'}` : `₹${o.reward_value} off`}`;
    return '';
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const inputStyle = { padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: '100%', boxSizing: 'border-box' };
  const label = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 };
  const btn = { padding: '0.42rem 0.8rem', borderRadius: 6, border: '1px solid #dcdde1', cursor: 'pointer', fontWeight: 700, fontSize: '0.84rem' };

  return (
    <div className="page-shell">
      {error && <div style={{ background: '#fef2f2', color: '#991b1b', padding: '0.5rem 0.8rem', borderRadius: 8, border: '1px solid #fecaca', marginBottom: '0.6rem' }}>{error}</div>}
      {message && <div style={{ background: '#ecfdf5', color: '#065f46', padding: '0.5rem 0.8rem', borderRadius: 8, border: '1px solid #a7f3d0', marginBottom: '0.6rem' }}>{message}</div>}

      <div className="filter-bar" style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ margin: '0 0 0.45rem', fontSize: '1rem' }}>Property & Outlet</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.7rem' }}>
          <input readOnly value={property?.name ? `${property.code || ''} ${property.name}`.trim() : (propertyId ? `Property ${propertyId.slice(0,8)}…` : 'No property selected')}
            style={{ ...inputStyle, background: '#f1f5f9' }} />
          <select value={outletId} onChange={(e) => setOutletId(e.target.value)} style={inputStyle}>
            <option value="">Select Outlet</option>
            {outlets.map(o => <option key={o.id} value={o.id}>{o.code} — {o.name} {o.outlet_type ? `(${o.outlet_type})` : ''}</option>)}
          </select>
        </div>
      </div>

      {!outletId ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Pick an outlet above to manage offers.</div>
      ) : editing ? (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1rem 1.2rem', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
          <h3 style={{ margin: '0 0 0.8rem' }}>{form.id ? `Edit Offer #${form.id}` : 'New Offer'}</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '0.8rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>Title (shown in customer banner)</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Buy 2 Beers, Get 40% Off" style={inputStyle} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>Description / fine print (optional)</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} />
            </div>
          </div>

          {/* Rule type */}
          <label style={label}>Rule Type</label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
            {RULE_TYPES.map(rt => (
              <button key={rt.key} type="button" onClick={() => setForm(f => ({ ...f, rule_type: rt.key }))}
                style={{
                  ...btn, padding: '0.5rem 0.9rem',
                  background: form.rule_type === rt.key ? '#1e40af' : '#fff',
                  color: form.rule_type === rt.key ? '#fff' : '#1e40af',
                  borderColor: form.rule_type === rt.key ? '#1e40af' : '#dbeafe',
                }}>
                {rt.icon} {rt.label}
              </button>
            ))}
          </div>

          {/* Trigger fields — dynamic per rule type */}
          <div style={{ background: '#f8fafc', padding: '0.8rem 1rem', borderRadius: 8, marginBottom: '0.8rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.5rem', color: '#334155' }}>Trigger (what customer must do)</div>
            {(form.rule_type === 'QTY_DISCOUNT' || form.rule_type === 'BOGO') && (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.6rem' }}>
                <div>
                  <label style={label}>Trigger Item</label>
                  <select value={form.trigger_item_id} onChange={e => setForm(f => ({ ...f, trigger_item_id: e.target.value }))} style={inputStyle}>
                    <option value="">Select item</option>
                    {menuItems.map(mi => <option key={mi.id} value={mi.id}>{mi.name} — ₹{mi.base_price || 0}</option>)}
                  </select>
                </div>
                <div>
                  <label style={label}>Min Qty</label>
                  <input type="number" min="1" value={form.trigger_qty} onChange={e => setForm(f => ({ ...f, trigger_qty: e.target.value }))} style={inputStyle} />
                </div>
              </div>
            )}
            {form.rule_type === 'SPEND_THRESHOLD' && (
              <div>
                <label style={label}>Minimum spend (₹)</label>
                <input type="number" min="0" value={form.trigger_amount} onChange={e => setForm(f => ({ ...f, trigger_amount: e.target.value }))} placeholder="800" style={inputStyle} />
              </div>
            )}
            {form.rule_type === 'COMBO_PRICE' && (
              <div>
                <label style={label}>Combo items (all must be in order)</label>
                {(form.trigger_items_json || []).map((line, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 60px', gap: '0.4rem', marginBottom: '0.35rem' }}>
                    <select value={line.menu_item_id} onChange={e => updateComboLine(idx, { menu_item_id: e.target.value })} style={inputStyle}>
                      <option value="">Select item</option>
                      {menuItems.map(mi => <option key={mi.id} value={mi.id}>{mi.name}</option>)}
                    </select>
                    <input type="number" min="1" value={line.qty} onChange={e => updateComboLine(idx, { qty: Number(e.target.value) })} style={inputStyle} />
                    <button type="button" onClick={() => removeComboLine(idx)} style={{ ...btn, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>✕</button>
                  </div>
                ))}
                <button type="button" onClick={addComboLine} style={{ ...btn, background: '#eff6ff', color: '#1e40af', border: '1px solid #dbeafe' }}>+ Add item</button>
              </div>
            )}
          </div>

          {/* Reward */}
          <div style={{ background: '#fffbeb', padding: '0.8rem 1rem', borderRadius: 8, marginBottom: '0.8rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.5rem', color: '#78350f' }}>Reward (what customer gets)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.6rem' }}>
              <div>
                <label style={label}>Reward Type</label>
                <select value={form.reward_type} onChange={e => setForm(f => ({ ...f, reward_type: e.target.value }))} style={inputStyle}>
                  {REWARD_TYPES.map(rt => <option key={rt.key} value={rt.key}>{rt.label}</option>)}
                </select>
              </div>
              {form.reward_type !== 'FREE_ITEM' && (
                <div>
                  <label style={label}>{form.reward_type === 'PERCENT' ? 'Percent (%)' : form.reward_type === 'BUNDLE_PRICE' ? 'Bundle Price (₹)' : 'Discount (₹)'}</label>
                  <input type="number" min="0" step="0.01" value={form.reward_value} onChange={e => setForm(f => ({ ...f, reward_value: e.target.value }))} style={inputStyle} />
                </div>
              )}
              {form.reward_type === 'FREE_ITEM' && (
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={label}>Free Item</label>
                  <select value={form.reward_item_id} onChange={e => setForm(f => ({ ...f, reward_item_id: e.target.value }))} style={inputStyle}>
                    <option value="">Select item</option>
                    {menuItems.map(mi => <option key={mi.id} value={mi.id}>{mi.name}</option>)}
                  </select>
                </div>
              )}
              {form.reward_type !== 'FREE_ITEM' && form.reward_type !== 'BUNDLE_PRICE' && (
                <div>
                  <label style={label}>Apply to</label>
                  <select value={form.reward_scope} onChange={e => setForm(f => ({ ...f, reward_scope: e.target.value }))} style={inputStyle}>
                    <option value="QUALIFYING_ITEMS">Qualifying items only</option>
                    <option value="ENTIRE_BILL">Entire bill</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Validity */}
          <div style={{ background: '#f8fafc', padding: '0.8rem 1rem', borderRadius: 8, marginBottom: '0.8rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.5rem', color: '#334155' }}>Validity (all optional)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.6rem', marginBottom: '0.5rem' }}>
              <div><label style={label}>Valid From</label><input type="date" value={form.valid_from} onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))} style={inputStyle} /></div>
              <div><label style={label}>Valid To</label><input type="date" value={form.valid_to} onChange={e => setForm(f => ({ ...f, valid_to: e.target.value }))} style={inputStyle} /></div>
              <div><label style={label}>Time From (happy hour)</label><input type="time" value={form.valid_time_from} onChange={e => setForm(f => ({ ...f, valid_time_from: e.target.value }))} style={inputStyle} /></div>
              <div><label style={label}>Time To</label><input type="time" value={form.valid_time_to} onChange={e => setForm(f => ({ ...f, valid_time_to: e.target.value }))} style={inputStyle} /></div>
            </div>
            <label style={label}>Days of week (leave empty = every day)</label>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
              {DAYS.map(d => (
                <button key={d} type="button" onClick={() => toggleDay(d)}
                  style={{
                    ...btn, padding: '0.3rem 0.65rem', fontSize: '0.76rem',
                    background: (form.valid_days_json || []).includes(d) ? '#1e40af' : '#fff',
                    color: (form.valid_days_json || []).includes(d) ? '#fff' : '#334155',
                  }}>{d}</button>
              ))}
            </div>
          </div>

          {/* Trigger surfaces + cap */}
          <div style={{ background: '#f0f9ff', padding: '0.8rem 1rem', borderRadius: 8, marginBottom: '0.8rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.5rem', color: '#1e40af' }}>Where customer sees this offer</div>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.show_on_cart} onChange={e => setForm(f => ({ ...f, show_on_cart: e.target.checked }))} />
                🛒 Live cart banner
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.show_on_checkout} onChange={e => setForm(f => ({ ...f, show_on_checkout: e.target.checked }))} />
                🧾 Request Bill modal
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.staff_pushable} onChange={e => setForm(f => ({ ...f, staff_pushable: e.target.checked }))} />
                👤 Staff can push
              </label>
            </div>
            <label style={label}>Max acceptances per customer session</label>
            <input type="number" min="1" value={form.max_per_session} onChange={e => setForm(f => ({ ...f, max_per_session: Number(e.target.value) }))} style={{ ...inputStyle, maxWidth: 180 }} />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', marginBottom: '0.8rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
            Active
          </label>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setEditing(false)} style={{ ...btn, background: '#fff', color: '#334155' }}>Cancel</button>
            <button type="button" onClick={save} disabled={loading} style={{ ...btn, background: '#1e40af', color: '#fff', border: 'none' }}>{loading ? 'Saving…' : (form.id ? 'Save changes' : 'Create offer')}</button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <h3 style={{ margin: 0 }}>Offers ({offers.length})</h3>
            <button onClick={startCreate} style={{ ...btn, background: '#1e40af', color: '#fff', border: 'none' }}>+ Add Offer</button>
          </div>
          {offers.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              No offers yet. <button onClick={startCreate} style={{ background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', textDecoration: 'underline' }}>Create your first offer</button>
            </div>
          )}
          {offers.map(o => (
            <div key={o.id} style={{ background: '#fff', border: `1px solid ${o.is_active ? '#e2e8f0' : '#fca5a5'}`, borderRadius: 10, padding: '0.8rem 1rem', marginBottom: '0.55rem', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>{o.title}</span>
                    {!o.is_active && <span style={{ padding: '2px 8px', background: '#fee2e2', color: '#991b1b', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700 }}>INACTIVE</span>}
                    <span style={{ padding: '2px 8px', background: '#e0e7ff', color: '#3730a3', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700 }}>{RULE_TYPES.find(r => r.key === o.rule_type)?.label || o.rule_type}</span>
                  </div>
                  <div style={{ color: '#475569', fontSize: '0.85rem', marginBottom: 4 }}>{describeOffer(o)}</div>
                  <div style={{ color: '#94a3b8', fontSize: '0.74rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {o.show_on_cart && <span>🛒 Cart</span>}
                    {o.show_on_checkout && <span>🧾 Checkout</span>}
                    {o.staff_pushable && <span>👤 Staff-push</span>}
                    {o.valid_time_from && <span>⏰ {String(o.valid_time_from).slice(0,5)}–{String(o.valid_time_to || '').slice(0,5)}</span>}
                    {Array.isArray(o.valid_days_json) && o.valid_days_json.length > 0 && <span>📅 {o.valid_days_json.join(',')}</span>}
                    <span>Cap {o.max_per_session || 1}/session</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button onClick={() => startEdit(o)} style={{ ...btn, background: '#eff6ff', color: '#1e40af', borderColor: '#dbeafe' }}>Edit</button>
                  {o.is_active && <button onClick={() => deactivate(o)} style={{ ...btn, background: '#fef2f2', color: '#991b1b', borderColor: '#fecaca' }}>Deactivate</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
